import { HttpFileServer } from '@application/http_file_server.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { isHttpError } from '@visiba/http';
import { Presentation } from '@visiba/presentation';
import { v4 } from 'uuid';
import { FileManagerService } from '../file_manager.service';
import { FileIdentity, IdentityResolver } from './_identity_resolver';
import { ResolvedResultState, WorkerTaskState } from './common';
export interface FileWorkerPresentationModel {
	fileState: WorkerTaskState;
	failedReason: Nullable<string>;
}

export class FileWorker {
	public readonly presentation = Presentation.create<FileWorkerPresentationModel>({
		fileState: WorkerTaskState.Pristine,
		failedReason: null,
	});
	public readonly id: string;
	public readonly fileName: string;
	public readonly fileType: string;
	public readonly fileSize: number;

	private previewImageSrc: Nullable<string> = null;
	private canCancelUpload: Nullable<AbortController> = null;
	private identityResolver: Nullable<IdentityResolver> = null;

	constructor(
		private readonly file: File,
		private readonly endpoint: string,
		private readonly httpFileServer: HttpFileServer,
	) {
		this.id = v4();
		this.fileName = file.name;
		this.fileSize = file.size;
		this.fileType = file.type === '' ? FileManagerService.resolveMimeType(file.name) : file.type;
	}

	public get downloadUrl(): string {
		const blob = this.getImageBlob();
		if (blob == null) return '';

		return blob;
	}

	/**
	 * If the worker is missing an identity, it'll try to resolve one for this worker.
	 */
	public async giveIdentity(): Promise<FileIdentity> {
		if (this.identityResolver == null) {
			this.identityResolver = new IdentityResolver(this.httpFileServer);
		}

		return await this.identityResolver.unwrapIdentity(this.endpoint, this.fileName, this.fileSize, this.fileType);
	}

	/**
	 * Uploads the file to the server.
	 */
	public async upload(): Promise<ResolvedResultState> {
		const identityTicket = this.isUploaded(true);
		if (identityTicket != null) {
			return [identityTicket, 'success'];
		}

		if (this.hasFailedUpload()) {
			return [null, 'failed'];
		}

		this.presentation.write({ fileState: WorkerTaskState.Uploading });

		let identity: FileIdentity;
		try {
			if (this.identityResolver != null) {
				identity = await this.identityResolver.unwrapIdentity(this.endpoint, this.fileName, this.fileSize, this.fileType);
			} else {
				identity = await this.giveIdentity();
			}
		} catch (error: unknown) {
			// TODO DEV-13650: expose a type of error from @visiba/http matching this.
			if ((error as { __CANCEL__: boolean }).__CANCEL__ === true) {
				this.presentation.write({ fileState: WorkerTaskState.UploadCancelled });

				return [null, 'cancelled'];
			} else {
				let failedReason: string | null = null;

				if (isHttpError<{ ErrorCode: string }>(error)) {
					const errors = error.data.ErrorCode?.split(', ') ?? [];
					if (errors.length) {
						if (errors.includes('File extension not allowed') || errors.includes('File mime type not allowed')) {
							failedReason = I18nAndL10n.translate('Feature.FileManager.FailedUploadReason.FileTypeNotAllowed');
						} else if (errors.includes('File too large')) {
							failedReason = I18nAndL10n.translate('Feature.FileManager.FailedUploadReason.FileSizeTooLarge', {
								maxFileSize: '100 mb',
							});
						} else {
							failedReason = I18nAndL10n.translate('Feature.FileManager.FailedUploadReason.Unknown');
						}
					}
				}

				this.presentation.write({ fileState: WorkerTaskState.UploadFailed, failedReason });
			}

			throw [null, 'unknown'];
		}

		const [request, cancel] = this.httpFileServer.upload(this.file, identity.uploadUrl);

		try {
			this.canCancelUpload = cancel;
			const uploadResult = await request;

			if (uploadResult.err) {
				this.presentation.write({
					fileState: WorkerTaskState.UploadFailed,
					failedReason: I18nAndL10n.translate('Feature.FileManager.FailedUploadReason.Unknown'),
				});

				throw [null, 'unknown'];
			}

			this.presentation.write({ fileState: WorkerTaskState.Uploaded });
		} catch (error: unknown) {
			// TODO DEV-13650: expose a type of error from @visiba/http matching this.
			if ((error as { __CANCEL__: boolean }).__CANCEL__ === true) {
				this.presentation.write({ fileState: WorkerTaskState.UploadCancelled });
			}

			return [null, 'cancelled'];
		} finally {
			this.canCancelUpload = null;
		}

		return [identity.id, 'success'];
	}

	/**
	 * Returns a binary state whether the file has been uploaded or not.
	 */
	public isUploaded(): boolean;
	/**
	 * Returns the files identity if the file has been uploaded, if not the return
	 * value will be null.
	 */
	public isUploaded(withReturnValue?: boolean): string | null;
	public isUploaded(withReturnValue?: boolean): boolean | string | null {
		const identity = this.identityResolver?.getIdentity();
		const isUploaded = this.presentation.read().fileState === WorkerTaskState.Uploaded;

		if (!withReturnValue) {
			return identity != null && isUploaded;
		}

		if (identity == null) {
			return null;
		}

		return isUploaded ? identity.id : null;
	}

	public hasFailedUpload(): boolean {
		return this.presentation.read().fileState === WorkerTaskState.UploadFailed;
	}

	/**
	 * Cancels the upload if it is in progress. If the upload is completed, it'll
	 * try to remove the file.
	 */
	public async drop(): Promise<void> {
		this.removeThumbnail();

		const identity = this.identityResolver?.getIdentity();
		if (identity == null) {
			this.identityResolver?.reset();
			this.presentation.write({ fileState: WorkerTaskState.UploadCancelled });

			return;
		}

		if (this.canCancelUpload) {
			this.canCancelUpload.abort();
			this.presentation.write({ fileState: WorkerTaskState.UploadCancelled });

			return;
		}

		const { fileState } = this.presentation.read();
		if (fileState === WorkerTaskState.Uploaded) {
			try {
				this.presentation.write({ fileState: WorkerTaskState.Deleting });
				await this.httpFileServer.remove(identity.deleteUrl);
				this.identityResolver = null;
				this.presentation.write({ fileState: WorkerTaskState.Deleted });
				// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
			} catch (error) {
				this.presentation.write({ fileState: WorkerTaskState.DeletionFailed });
			}
		}
	}

	/**
	 * Taps into the workers identity resolver to get the identity of the file, or
	 * null if the identiy is not yet resolved.
	 *
	 * @returns FileIdentity | null
	 */
	public getIdentity(): FileIdentity | null {
		return this.identityResolver?.getIdentity() ?? null;
	}

	/**
	 * Returns which state the file assigned to the worker is in.
	 *
	 * @returns WorkerTaskState
	 */
	public getFileTaskState(): WorkerTaskState {
		return this.presentation.read().fileState;
	}

	public getThumbnail(): Nullable<string> {
		if (!this.fileType.includes('image')) return null;

		return this.getImageBlob();
	}

	public removeThumbnail(): void {
		if (this.previewImageSrc != null) {
			URL.revokeObjectURL(this.previewImageSrc);
			this.previewImageSrc = null;
		}
	}

	private getImageBlob(): Nullable<string> {
		if (this.previewImageSrc == null) {
			this.previewImageSrc = URL.createObjectURL(this.file);
		}

		return this.previewImageSrc;
	}
}
